// Mock data for Options Analogies website

export const glossaryTerms = [
  {
    id: 1,
    term: 'Option',
    definition: 'Contract granting a right (buyer) and an obligation (seller) at a strike by expiration.'
  },
  {
    id: 2,
    term: 'Call',
    definition: 'Right to buy shares at the strike price by expiration.'
  },
  {
    id: 3,
    term: 'Put',
    definition: 'Right to sell shares at the strike price by expiration.'
  },
  {
    id: 4,
    term: 'Sell to Open (Short)',
    definition: 'You are the insurer; you collect premium and accept obligations.'
  },
  {
    id: 5,
    term: 'Premium',
    definition: 'Upfront price of the option contract.'
  },
  {
    id: 6,
    term: 'Strike Price',
    definition: 'Pre-agreed transaction price in the option contract.'
  },
  {
    id: 7,
    term: 'Expiration',
    definition: 'Contract end date when the option expires or must be exercised.'
  },
  {
    id: 8,
    term: 'ITM (In-The-Money)',
    definition: 'When exercising the option would be profitable at current price.'
  },
  {
    id: 9,
    term: 'OTM (Out-of-The-Money)',
    definition: 'When exercising the option would not be profitable at current price.'
  },
  {
    id: 10,
    term: 'Assignment',
    definition: 'Seller must perform: deliver shares (short call) or buy shares (short put).'
  },
  {
    id: 11,
    term: 'Breakeven',
    definition: 'CSP = strike − premium. CC effective basis = cost − premium.'
  },
  {
    id: 12,
    term: 'Implied Volatility (IV)',
    definition: 'Market-implied future volatility; higher IV → higher premiums.'
  },
  {
    id: 13,
    term: 'Theta (Time Decay)',
    definition: "Option value's erosion over time as expiration approaches."
  },
  {
    id: 14,
    term: 'Early Exercise',
    definition: 'Possible exercise before expiration, especially around dividends on calls.'
  },
  {
    id: 15,
    term: 'Buy-Write',
    definition: 'Buy stock and sell a call simultaneously (covered call setup).'
  }
];

export const cspAnalogies = [
  {
    id: 1,
    title: 'Insurance (Auto Collision)',
    category: 'CSP',
    story: "You're the insurer writing collision coverage. If there's an accident (price drop below strike), you pay out by buying the 'totaled car' (shares at strike). Collect premiums while the policy is active.",
    pros: ['Paid upfront premium', 'Defines your buy price', 'Compensated for risk taken'],
    cons: ['Large claims on crashes', 'Capital tied up as reserves', 'Limited upside (premium only)'],
    formula: 'Max Profit = Premium | Breakeven = Strike − Premium',
    videoUrl: 'https://youtu.be/jWtU-goXlDw'
  },
  {
    id: 2,
    title: 'Paid Limit Order',
    category: 'CSP',
    story: 'You want to buy shares at $X anyway. Instead of placing a regular limit order, you sell a put and get paid to wait. The market compensates you for your willingness to buy at that price.',
    pros: ['Income while waiting for entry', 'Patient capital deployment', 'Disciplined buy price'],
    cons: ['May never get filled if price stays above', 'Get filled exactly when it hurts (falling knife)', 'Miss rallies while waiting'],
    formula: 'Premium Collected = Income | Assignment = Buy at Strike',
    videoUrl: 'https://youtu.be/IYmZjGR-6cQ'
  },
  {
    id: 3,
    title: 'Earnest-Money Buyer of Last Resort',
    category: 'CSP',
    story: 'Like real estate option fees—you receive money now for the right/obligation to purchase if conditions are met. If the property value (stock price) drops to the agreed level, you must buy.',
    pros: ['Fee collected upfront', 'Defines entry price', 'Obligation matches intent'],
    cons: ['Must honor commitment in downturn', 'Capital reserved during term', 'No refunds on obligation'],
    formula: 'Cash Reserved = Strike × 100 | Max Loss = Strike − Premium',
    videoUrl: 'https://youtu.be/bSqj4kZ0a7s'
  },
  {
    id: 4,
    title: 'Rain Check Issuer',
    category: 'CSP',
    story: 'You issue rain checks promising items at a sale price if the sale returns. Customers pay a small fee for this guarantee. If it "rains" (price drops), everyone redeems their checks and you must deliver.',
    pros: ['Keep fees if conditions not met', 'Predictable obligation', 'Steady income stream'],
    cons: ['Mass redemption in adverse conditions', 'Inventory forced at worst time', 'Limited profit (fee only)'],
    formula: 'Income = Premium × Contracts | Risk = Strike Value'
  },
  {
    id: 5,
    title: 'Warranty Seller with Cash Reserves',
    category: 'CSP',
    story: 'You sell extended warranties and hold cash reserves for claims. If products "fail" (price drops below strike), you pay out by purchasing shares. Rare events but significant payouts.',
    pros: ['Predictable income', 'Clear risk parameters', 'Actuarial approach'],
    cons: ['Rare but large claims', 'Dead capital in reserves', 'Underestimating risk hurts'],
    formula: 'Reserve = Strike × 100 | Profit = Premium if No Assignment'
  }
];

export const ccAnalogies = [
  {
    id: 6,
    title: 'Insurance (Agreed-Value Property)',
    category: 'CC',
    story: 'You own property and pre-agree to sell at a set value if an "event" (rally beyond strike) occurs. Collect premium for this commitment. If the event happens, you sell at the agreed price regardless of market value.',
    pros: ['Income on existing asset', 'Planned exit strategy', 'Downside buffer from premium'],
    cons: ['Upside capped', 'Miss extraordinary gains', 'Still bear most downside'],
    formula: 'Max Profit = (Strike − Cost) + Premium | Cap at Strike',
    videoUrl: 'https://youtu.be/KYXPT1b8ago'
  },
  {
    id: 7,
    title: 'Right of First Refusal (ROFR) You Sold',
    category: 'CC',
    story: 'You own a house and someone pays for the right to buy it at $X within a timeframe. If market heats up above that price, they exercise and you must sell. You keep the fee either way.',
    pros: ['Income on idle asset', 'Set your desired exit', 'Fee is yours regardless'],
    cons: ['Must honor price if market soars', 'Opportunity cost', 'Asset called away at worst time'],
    formula: 'New Basis = Cost − Premium | Capped Gain = Strike',
    videoUrl: 'https://youtu.be/LQoe4IfOtEA'
  },
  {
    id: 8,
    title: 'Reserved Listing with a Cap',
    category: 'CC',
    story: 'A car buyer pays to reserve buying your car at $X. If market value spikes above $X, they claim it at the reserved price. You accept the certainty of $X plus the reservation fee.',
    pros: ['Certainty of exit price', 'Immediate income', 'Reduces emotion'],
    cons: ['Miss extra upside potential', 'Forced sale in rally', 'Limited to strike price'],
    formula: 'Effective Sale = Strike + Premium | Upside Capped'
  },
  {
    id: 9,
    title: 'Season Pass with Buy-Back Clause',
    category: 'CC',
    story: 'You own premium seats and someone pays for the right to claim them for any sold-out game at a fixed price. Earn ongoing income but lose the seats on the best nights when demand spikes.',
    pros: ['Ongoing income stream', 'Monetize unused asset', 'Predictable terms'],
    cons: ['Lose asset when value peaks', 'Best events claimed', 'Upside sacrificed for income'],
    formula: 'Income = Premium | Give Up Seats (Shares) if ITM'
  },
  {
    id: 10,
    title: 'Parking Meter vs. Tow Risk',
    category: 'CC',
    story: 'Earn meter fees (premium) while your car is parked (holding shares). If a parade comes through (price spikes above strike), your spot is claimed and the car is "towed" (shares called away).',
    pros: ['Predictable income drips', 'Known maximum outcome', 'Reduces holding cost'],
    cons: ['Towed at worst time—peak value', 'Miss the parade (rally)', 'Only meter fees as consolation'],
    formula: 'Meter Fees = Premium | Tow Price = Strike'
  },
  {
    id: 11,
    title: 'Crop Hedge Lite',
    category: 'CC',
    story: 'Farmers pre-sell part of harvest to lock in a price. Provides stability and income certainty, but forfeit windfalls if crop prices soar due to shortages or high demand.',
    pros: ['Stabilizes income', 'Reduces uncertainty', 'Locks acceptable price'],
    cons: ['Forfeits windfall prices', 'Capped at contract price', 'Regret in boom years'],
    formula: 'Locked Price = Strike | Premium = Certainty Cost'
  },
  {
    id: 12,
    title: 'Airline Overbooking',
    category: 'CC',
    story: 'Commit your seat for compensation. If flight is overbooked (price above strike), your seat is taken at the fixed compensation amount. You cannot enjoy the unexpected upgrade to first class.',
    pros: ['Compensation now', 'Clear terms', 'Works for routine flights'],
    cons: ['No upgrade when it matters', 'Fixed payout', 'Miss premium experience'],
    formula: 'Compensation = Premium | Seat Lost if Overbooked (ITM)'
  }
];

export const allAnalogies = [...cspAnalogies, ...ccAnalogies];

export const faqs = [
  {
    id: 1,
    question: 'Is premium "free money"?',
    answer: 'No; it compensates for real obligations and risk. The premium you collect represents payment for accepting specific contractual obligations—either to buy shares (CSP) or sell shares (CC) at predetermined prices. Higher premiums reflect higher perceived risk.'
  },
  {
    id: 2,
    question: 'Why do people lose with the Wheel?',
    answer: 'Bad underwriting, chasing yield, picking strikes they don\'t truly want, ignoring trends/earnings. Many traders focus solely on collecting premiums without considering if they actually want to own the stock at that price or if market conditions support the strategy.'
  },
  {
    id: 3,
    question: 'What about taxes?',
    answer: 'Location-specific; short-term gains common—consult a tax pro. Options premiums and assignment events often trigger short-term capital gains. Tax treatment varies by jurisdiction, holding period, and whether options are exercised or expire. Professional guidance is essential.'
  },
  {
    id: 4,
    question: 'Can options be exercised early?',
    answer: 'Yes, especially near ex-dividend dates on calls. American-style options can be exercised any time before expiration. Call holders might exercise early to capture dividends. Put holders might exercise early if deep ITM. Always be prepared for early assignment.'
  },
  {
    id: 5,
    question: 'How do I choose strike and expiration?',
    answer: 'Match to thesis, volatility, and risk tolerance. Shorter terms = faster theta decay but require active management. Longer terms = bigger premiums but slower decay and more capital commitment. Choose strikes you\'re genuinely comfortable with for assignment scenarios.'
  },
  {
    id: 6,
    question: 'What happens at expiration if my option is ITM?',
    answer: 'Automatic assignment typically occurs. For short puts (CSP), you\'ll be obligated to buy 100 shares per contract at the strike price. For short calls (CC), your shares will be sold at the strike. Most brokers auto-exercise ITM options at expiration.'
  },
  {
    id: 7,
    question: 'Do I need margin to sell options?',
    answer: 'CSPs require cash reserves (strike × 100 per contract), not margin. Covered calls require owning the shares. Both are "secured" strategies. However, uncovered/naked options do require margin approval and carry unlimited risk—not covered in this educational material.'
  },
  {
    id: 8,
    question: 'What if the stock gaps down significantly?',
    answer: 'Premium provides limited protection. If you sold a CSP at $50 strike for $1 premium and the stock gaps to $40, you\'re assigned at $50 with an effective basis of $49—still $9 underwater. The premium softens but doesn\'t erase large moves.'
  }
];

export const wheelSteps = [
  {
    id: 1,
    step: 'Acquire Inventory Thoughtfully (CSP)',
    description: 'Sell cash-secured puts on stocks you want to own. Collect premium and potentially acquire shares at a discount to current price. Only choose strikes where you\'re genuinely happy to own the stock.',
    icon: 'ShoppingCart'
  },
  {
    id: 2,
    step: 'Price Your Shelf (CC)',
    description: 'Once assigned shares, immediately sell covered calls. Collect premium and set your desired exit price (strike). Choose strikes where you\'re happy to sell and take profit.',
    icon: 'Tag'
  },
  {
    id: 3,
    step: 'Rinse & Repeat',
    description: 'If shares are called away, return to step 1. If not called, roll the covered call or let it expire and sell a new one. Adjust strikes and expiries based on market conditions and your thesis.',
    icon: 'RefreshCw'
  }
];

export const wheelPitfalls = [
  'Selling strikes you don\'t actually want (poor underwriting)',
  'Ignoring breakevens and assignment scenarios',
  'Over-long expiries with dead capital',
  'Under-estimating gap risk (earnings, macro shocks)',
  'Chasing yield without regard for underlying quality',
  'Failing to adjust strategy in strong trends'
];